/**
 * Mastery Module — Public API
 *
 * Single entry point for all mastery-related functionality.
 * Consolidates 8 files previously scattered across src/utils/.
 *
 * Architecture:
 * - familyMasteryEngine: ELO-based mastery scoring per family
 * - masteryCanonical: Canonical mastery queries (module, tier, family)
 * - masteryDecay: Time-based forgetting curve
 * - adaptiveEngine: Question selection algorithm (v11.2)
 * - placementEngine: Diagnostic placement tests
 * - questionAttemptHistory: Per-question attempt tracking (NOT mastery)
 * - skillAggregation: Skill-level mastery aggregation for UI
 */

// ── Family Mastery Engine ────────────────────────────────────────────────────
export {
  updateFamilyMastery,
  getFamilyMasteryKey,
  parseMasteryKey,
  isGroupScopedKey,
} from './familyMasteryEngine'
export type { FamilyAttempt, FamilyMasteryState } from './familyMasteryEngine'

// ── Mastery Canonical (Single Source of Truth) ───────────────────────────────
export {
  getFamilyMasteryScore,
  isFamilyMastered,
  getFamilyMasteryLevel,
  getMasteryLevel,
  getMasteryColor,
  getMasteryBgColor,
  getModuleMasteryFromFamilies,
  isModuleMasteredByFamilies,
  getTierMasteryFromFamilies,
  getAllTierMasteryFromFamilies,
  getStrugglingFamiliesForModule,
  getMasteredFamiliesForModule,
  getWeakestFamiliesForModule,
  getWeakestFamiliesGlobal,
  countFamiliesByMasteryLevel,
} from './masteryCanonical'

// ── Mastery Decay ────────────────────────────────────────────────────────────
export {
  computeDecayedMastery,
  hasActiveDecay,
  getDecayStatus,
  getDecayPercentage,
  computeAllDecayedMastery,
  MASTERY_DECAY_CONFIG,
} from './masteryDecay'
export type { MasteryDecayConfig } from './masteryDecay'

// ── Adaptive Engine ──────────────────────────────────────────────────────────
export {
  getAdaptiveSessionV11,
  generateSelectionDebugReport,
} from './adaptiveEngine'
export type { SelectionDebugReport } from './adaptiveEngine'

// ── Tier Progress ────────────────────────────────────────────────────────────
export { computeTierProgress } from './tierProgress'
export type { TierProgressInfo } from './tierProgress'

// ── Sub-Skill Mastery ────────────────────────────────────────────────────────
export { computeSubSkillMastery } from './subSkillMastery'

// ── Placement Engine ─────────────────────────────────────────────────────────
export {
  getPlacementKey,
  resolvePhaseByDate,
  getAutoStartingTier,
  getAutoPhaseLabel,
} from './placementEngine'
export type { SchoolYearPhase } from './placementEngine'

// ── Question Attempt History ─────────────────────────────────────────────────
export {
  updateQuestionState,
  getQuestionStateKey,
  getAnsweredQuestionsForGroup,
  getRecentQuestionsForGroup,
  getReviewQueueForGroup,
  getReviewNeededCountForGroup,
  getQuestionsByStatus,
  getModuleStats,
  evictStaleAttempts,
  getAttemptHistorySize,
} from './questionAttemptHistory'
export type { EvictionReport } from './questionAttemptHistory'

// ── Skill Aggregation ────────────────────────────────────────────────────────
export {
  computeSkillMasteries,
  getActiveSkillCount,
  getSkillToSubSkillMap,
} from './skillAggregation'
export type { SkillMastery } from './skillAggregation'

// ── Growth Stages (mastery visualization) ────────────────────────────────────
export { getGrowthStage } from './growthStages'
export type { GrowthStage } from './growthStages'

// ── Selection Config (public constants) ───────────────────────────────────────
export { V11_CONFIG } from './selection/selectionConfig'

// ── Domain Types (knowledge state + placement) ───────────────────────────────
export type {
  QuestionStatus,
  QuestionState,
  QuestionAttempt,
  SubSkillProgress,
  PlacementResult,
} from './types'
